import { useQuery, useQueryClient } from "@tanstack/react-query";
import { collection, doc, getDocs, getFirestore, runTransaction } from "firebase/firestore";
import { type ChangeEvent, type FormEvent, useCallback, useMemo, useState } from "react";
import { AiOutlineUser } from "react-icons/ai";
import { BsTelephone } from "react-icons/bs";
import { FaRegAddressBook, FaRoute } from "react-icons/fa";
import { HiOutlineIdentification } from "react-icons/hi2";
import { IoLocationOutline, IoStorefrontOutline } from "react-icons/io5";
import { toast } from "react-toastify";
import { useClientesActions, useClientesState } from "../../../context/AppContext";
import {
  guardarClienteEnFirebase,
  uploadImage,
  uploadImageTienda,
} from "../../../pages/HomePage/Components/firebaseFunctions";
import { queryKeys } from "../../../queries/queryKeys";
import {
  calcularFechaFinalCredito,
  calcularPagoDiarioCredito,
  formatearFechaActualIso,
  formatearFechaDisplayDesdeIso,
  leerArchivoComoPreview,
  normalizarNumeroSeguro,
  OPCIONES_FORMA_PAGO_CREDITO,
} from "../../../utils/clienteCredito";
import {
  aplicarMovimientoARutaLocal,
  construirActualizacionRutaConMovimiento,
  crearMovimientoRutaData,
  crearMovimientoRutaRef,
} from "../../../utils/rutaMovimientos";
import type {
  CampoEntradaConfig,
  ClienteExistente,
  CrearClienteExistenteModalProps,
  DatosClienteForm,
  FormaDePago,
  GuardarClientePayload,
  UseCrearClienteExistenteModalReturn,
} from "./types";

const OPCIONES_FORMA_PAGO: FormaDePago[] = [...OPCIONES_FORMA_PAGO_CREDITO];

const crearDatosClienteInicial = (fechaUltimoAbono: string): DatosClienteForm => ({
  abono: 0,
  actualizado: new Date().toDateString(),
  cpf: "",
  cuotasAtrasadas: 0,
  cuotasPactadas: 1,
  cuotasPagadas: 0,
  descripcion: "",
  direccion: "",
  direccionCobro: "",
  fechaFinal: "",
  fechaUltimoAbono,
  formaDePago: "diario",
  historialPagos: [],
  image: undefined,
  imageTienda: undefined,
  nombreCliente: "",
  pagoDiario: 0,
  porcentajeInteres: 0,
  telefono: "",
  totalAbono: 0,
  ubicacion: "",
  valorPico: 0,
  valorPrestamo: 1,
});

const obtenerMetricasCredito = (datosCliente: DatosClienteForm) => {
  const valorPrestamo = normalizarNumeroSeguro(datosCliente.valorPrestamo);
  const porcentajeInteres = normalizarNumeroSeguro(datosCliente.porcentajeInteres);
  const cuotasPactadas = normalizarNumeroSeguro(datosCliente.cuotasPactadas);

  return {
    cuotasPactadas,
    porcentajeInteres,
    valorPrestamo,
  };
};

const recalcularDatosCredito = (
  datosCliente: DatosClienteForm,
  fechaInicialState: string,
): DatosClienteForm => {
  const { cuotasPactadas, porcentajeInteres, valorPrestamo } = obtenerMetricasCredito(datosCliente);

  return {
    ...datosCliente,
    fechaFinal: calcularFechaFinalCredito({
      cuotasPactadas,
      fechaBase: fechaInicialState,
      formaDePago: datosCliente.formaDePago,
    }),
    pagoDiario: calcularPagoDiarioCredito(valorPrestamo, porcentajeInteres, cuotasPactadas),
  };
};

const mapearClienteExistente = (cliente: ClienteExistente): Partial<DatosClienteForm> => ({
  cpf: cliente.cpf ?? "",
  descripcion: cliente.descripcion ?? "",
  direccion: cliente.direccion ?? "",
  direccionCobro: cliente.direccionCobro ?? "",
  image: cliente.imageUrl ?? "",
  imageTienda: cliente.imageTienda ?? "",
  nombreCliente: cliente.nombreCliente ?? "",
  telefono: cliente.telefono ?? "",
  ubicacion: cliente.ubicacion ?? "",
});

const obtenerErrorValidacion = (datosCliente: DatosClienteForm): string | null => {
  if (datosCliente.valorPrestamo <= 0) {
    return "El VALOR PRESTAMO debe ser mayor que cero (0)";
  }

  if (datosCliente.porcentajeInteres <= 0) {
    return "El PORCENTAJE DE INTERES debe ser mayor que cero (0)";
  }

  if (datosCliente.cuotasPactadas <= 0) {
    return "Las CUOTAS PACTADAS deben ser mayor que cero (0)";
  }

  return null;
};

const fetchAllClientsDeRuta = async (
  adminUid: string,
  rutaUid: string,
): Promise<ClienteExistente[]> => {
  const db = getFirestore();
  const querySnapshot = await getDocs(
    collection(db, "admin_users", adminUid, "rutas", rutaUid, "allClients"),
  );

  return querySnapshot.docs.map((clientDoc) => ({
    uid: clientDoc.id,
    ...(clientDoc.data() as ClienteExistente),
  }));
};

const crearPayloadCliente = (
  datosCliente: DatosClienteForm,
  fechaInicialState: string,
  imageUrl: File | string | null | undefined,
  imageTiendaUrl: File | string | null | undefined,
): GuardarClientePayload => {
  const { cuotasPactadas, porcentajeInteres, valorPrestamo } = obtenerMetricasCredito(datosCliente);
  const pagoDiario = Math.ceil(
    calcularPagoDiarioCredito(valorPrestamo, porcentajeInteres, cuotasPactadas),
  );

  const datosClienteActualizados: DatosClienteForm = {
    ...datosCliente,
    fechaFinal: calcularFechaFinalCredito({
      cuotasPactadas,
      fechaBase: fechaInicialState,
      formaDePago: datosCliente.formaDePago,
    }),
    pagoDiario,
  };

  return {
    datosCliente: datosClienteActualizados,
    imageTiendaUrl,
    imageUrl,
  };
};

export const useCrearClienteExistenteModal = ({
  searchTermExis,
  setIsModalCreateClienteExistente,
  setSearchTermExis,
  setUsuarioRuta,
  usuarioRuta,
}: CrearClienteExistenteModalProps): UseCrearClienteExistenteModalReturn => {
  const { infoClientes } = useClientesState();
  const { setInfoClientes } = useClientesActions();
  const queryClient = useQueryClient();
  const [imagePreview, setImagePreview] = useState<string | ArrayBuffer | null>(null);
  const [imageTiendaPreview, setImageTiendaPreview] = useState<string | ArrayBuffer | null>(null);
  const [fechaInicialState, setFechaInicialState] = useState(formatearFechaActualIso());
  const [selectedCliente, setSelectedCliente] = useState<ClienteExistente | null>(null);
  const [datosCliente, setDatosCliente] = useState<DatosClienteForm>(() =>
    crearDatosClienteInicial(new Date().toDateString()),
  );
  const [isSubmiting, setIsSubmiting] = useState(false);

  // Baja volatilidad (solo cambia al archivar/reactivar un cliente); reabrir
  // este modal para la misma ruta ya vista en la sesion sirve desde cache en
  // vez de repetir el fetch completo de la coleccion allClients.
  const { data: allClients = null } = useQuery({
    enabled: Boolean(usuarioRuta.adminUid && usuarioRuta.uid),
    gcTime: 30 * 60 * 1000,
    queryFn: () => fetchAllClientsDeRuta(usuarioRuta.adminUid as string, usuarioRuta.uid as string),
    queryKey: queryKeys.allClients(usuarioRuta.adminUid, usuarioRuta.uid),
    staleTime: 10 * 60 * 1000,
  });

  const filteredClients = useMemo(() => {
    const normalizedSearchTerm = searchTermExis.trim().toLowerCase();

    if (!normalizedSearchTerm || !allClients) {
      return [];
    }

    return allClients.filter((cliente) =>
      cliente.nombreCliente?.toLowerCase().includes(normalizedSearchTerm),
    );
  }, [allClients, searchTermExis]);

  const fechaInicialDisplay = useMemo(
    () => formatearFechaDisplayDesdeIso(fechaInicialState),
    [fechaInicialState],
  );
  const showInputSearch = selectedCliente === null;

  const handleClose = useCallback(() => {
    setIsModalCreateClienteExistente(false);
  }, [setIsModalCreateClienteExistente]);

  const actualizarDatosCliente = useCallback(
    (updater: (current: DatosClienteForm) => DatosClienteForm): void => {
      setDatosCliente(updater);
    },
    [],
  );

  const actualizarDatosCredito = useCallback(
    (updater: (current: DatosClienteForm) => DatosClienteForm): void => {
      actualizarDatosCliente((current) =>
        recalcularDatosCredito(updater(current), fechaInicialState),
      );
    },
    [actualizarDatosCliente, fechaInicialState],
  );

  const handleSelectedCliente = useCallback(
    (cliente: ClienteExistente) => {
      setSelectedCliente(cliente);
      actualizarDatosCliente((current) => ({
        ...current,
        ...mapearClienteExistente(cliente),
      }));
      setSearchTermExis("");
    },
    [actualizarDatosCliente, setSearchTermExis],
  );

  const handleValorPrestamoChange = useCallback(
    (event: ChangeEvent<HTMLInputElement>) => {
      const valorPrestamo = parseInt(event.target.value, 10);

      actualizarDatosCredito((current) => ({
        ...current,
        valorPrestamo: Number.isNaN(valorPrestamo) ? 0 : valorPrestamo,
      }));
    },
    [actualizarDatosCredito],
  );

  const handlePorcentajeInteresChange = useCallback(
    (event: ChangeEvent<HTMLInputElement>) => {
      const porcentajeInteres = parseFloat(event.target.value);

      actualizarDatosCredito((current) => ({
        ...current,
        porcentajeInteres: Number.isNaN(porcentajeInteres) ? 0 : porcentajeInteres,
      }));
    },
    [actualizarDatosCredito],
  );

  const handleCuotasPactadasChange = useCallback(
    (event: ChangeEvent<HTMLInputElement>) => {
      const cuotasPactadas = parseInt(event.target.value, 10);

      actualizarDatosCredito((current) => ({
        ...current,
        cuotasPactadas: Number.isNaN(cuotasPactadas) ? 0 : cuotasPactadas,
      }));
    },
    [actualizarDatosCredito],
  );

  const handleFormaDePagoChange = useCallback(
    (event: ChangeEvent<HTMLSelectElement>) => {
      const formaDePago = event.target.value as FormaDePago;

      actualizarDatosCredito((current) => ({
        ...current,
        formaDePago,
      }));
    },
    [actualizarDatosCredito],
  );

  const handleFechaInicialChange = useCallback(
    (event: ChangeEvent<HTMLInputElement>) => {
      const newFechaInicialState = event.target.value;

      setFechaInicialState(newFechaInicialState);
      actualizarDatosCliente((current) => recalcularDatosCredito(current, newFechaInicialState));
    },
    [actualizarDatosCliente],
  );

  const crearFileChangeHandler = useCallback(
    (field: "image" | "imageTienda", setPreview: (value: string | ArrayBuffer | null) => void) =>
      (event: ChangeEvent<HTMLInputElement>) => {
        const file = event.target.files?.[0];

        if (!file) {
          return;
        }

        actualizarDatosCliente((current) => ({
          ...current,
          [field]: file,
        }));
        leerArchivoComoPreview(file, setPreview);
      },
    [actualizarDatosCliente],
  );

  const handleImageChange = useMemo(
    () => crearFileChangeHandler("image", setImagePreview),
    [crearFileChangeHandler],
  );

  const handleImageTiendaChange = useMemo(
    () => crearFileChangeHandler("imageTienda", setImageTiendaPreview),
    [crearFileChangeHandler],
  );

  const handleRemoveImage = useCallback(() => {
    setImagePreview(null);
    actualizarDatosCliente((current) => ({
      ...current,
      image: null,
    }));
  }, [actualizarDatosCliente]);

  const handleRemoveImageTienda = useCallback(() => {
    setImageTiendaPreview(null);
    actualizarDatosCliente((current) => ({
      ...current,
      imageTienda: null,
    }));
  }, [actualizarDatosCliente]);

  const obtenerUbicacionActual = useCallback(() => {
    if (!navigator.geolocation) {
      console.error("La geolocalizacion no esta soportada por este navegador.");
      return;
    }

    navigator.geolocation.getCurrentPosition(
      (position) => {
        const { latitude, longitude } = position.coords;

        actualizarDatosCliente((current) => ({
          ...current,
          ubicacion: `${latitude}, ${longitude}`,
        }));
      },
      (error) => {
        console.error("Error obteniendo la ubicacion:", error);
      },
    );
  }, [actualizarDatosCliente]);

  const handleSearchTermChange = useCallback(
    (event: ChangeEvent<HTMLInputElement>) => {
      setSearchTermExis(event.target.value);
    },
    [setSearchTermExis],
  );

  const handleCampoTextoChange = useCallback(
    (
      field:
        | "cpf"
        | "nombreCliente"
        | "direccionCobro"
        | "telefono"
        | "direccion"
        | "descripcion"
        | "ubicacion",
    ) =>
      (event: ChangeEvent<HTMLInputElement>) => {
        const value = event.target.value;

        actualizarDatosCliente((current) => ({
          ...current,
          [field]: value,
        }));
      },
    [actualizarDatosCliente],
  );

  const campos = useMemo<CampoEntradaConfig[]>(
    () => [
      {
        Icon: HiOutlineIdentification,
        onChange: handleCampoTextoChange("cpf"),
        placeholder: "CPF",
        valor: datosCliente.cpf,
      },
      {
        Icon: AiOutlineUser,
        onChange: handleCampoTextoChange("nombreCliente"),
        placeholder: "Nombre del Cliente",
        valor: datosCliente.nombreCliente,
      },
      {
        Icon: FaRoute,
        onChange: handleCampoTextoChange("direccionCobro"),
        placeholder: "Direccion de Cobro",
        valor: datosCliente.direccionCobro,
      },
      {
        Icon: BsTelephone,
        onChange: handleCampoTextoChange("telefono"),
        placeholder: "Telefono",
        valor: datosCliente.telefono,
      },
      {
        Icon: FaRegAddressBook,
        onChange: handleCampoTextoChange("direccion"),
        placeholder: "Direccion de Residencia",
        valor: datosCliente.direccion,
      },
      {
        Icon: IoStorefrontOutline,
        onChange: handleCampoTextoChange("descripcion"),
        placeholder: "Descripcion de la tienda",
        valor: datosCliente.descripcion,
      },
      {
        Icon: IoLocationOutline,
        onChange: handleCampoTextoChange("ubicacion"),
        onClick: obtenerUbicacionActual,
        placeholder: "Ubicacion Actual",
        readOnly: true,
        valor: datosCliente.ubicacion,
      },
    ],
    [
      datosCliente.cpf,
      datosCliente.descripcion,
      datosCliente.direccion,
      datosCliente.direccionCobro,
      datosCliente.nombreCliente,
      datosCliente.telefono,
      datosCliente.ubicacion,
      handleCampoTextoChange,
      obtenerUbicacionActual,
    ],
  );

  const crearCliente = useCallback(
    async (event: FormEvent<HTMLFormElement>) => {
      event.preventDefault();
      setIsSubmiting(true);

      const errorValidacion = obtenerErrorValidacion(datosCliente);

      if (errorValidacion) {
        setIsSubmiting(false);
        toast.error(errorValidacion);
        return;
      }

      try {
        if (!selectedCliente?.uid) {
          throw new Error("Selecciona un cliente existente");
        }

        const [imageTiendaUrl, imageUrl] = await Promise.all([
          datosCliente.imageTienda instanceof File
            ? uploadImageTienda({
                imageTienda: datosCliente.imageTienda,
                descripcion: datosCliente.descripcion,
              })
            : Promise.resolve(datosCliente.imageTienda ?? null),
          datosCliente.image instanceof File
            ? uploadImage({
                image: datosCliente.image,
                nombre: datosCliente.nombreCliente,
              })
            : Promise.resolve(datosCliente.image ?? null),
        ]);

        const db = getFirestore();

        if (!usuarioRuta.adminUid || !usuarioRuta.uid) {
          throw new Error("Ruta invalida");
        }

        const rutaRef = doc(db, "admin_users", usuarioRuta.adminUid, "rutas", usuarioRuta.uid);
        const fechaMovimiento = new Date();
        const movimientoRef = crearMovimientoRutaRef(db, usuarioRuta.adminUid, usuarioRuta.uid);
        const resultadoTransaccion = await runTransaction(db, async (transaction) => {
          const rutaSnapshot = await transaction.get(rutaRef);
          const rutaData = rutaSnapshot.data();

          if (!rutaData) {
            throw new Error("No se encontro la informacion de la ruta");
          }

          const saldoViejoNum = Number(rutaData.saldoInicial ?? 0);
          const saldoNuevo = saldoViejoNum - datosCliente.valorPrestamo;
          const movimiento = crearMovimientoRutaData({
            descripcion: "prestamo",
            fecha: fechaMovimiento,
            monto: datosCliente.valorPrestamo,
            origen: "crear_cliente_existente",
            responsable: rutaData.responsable ?? "Admin",
            saldoDespues: saldoNuevo,
          });
          const rutaUpdates = construirActualizacionRutaConMovimiento(
            rutaData,
            movimiento,
            saldoNuevo,
          );

          transaction.set(movimientoRef, movimiento);
          transaction.update(rutaRef, rutaUpdates);

          return {
            movimiento,
            saldoNuevo,
          };
        });

        setUsuarioRuta((previousUsuarioRuta) =>
          previousUsuarioRuta
            ? aplicarMovimientoARutaLocal(
                previousUsuarioRuta,
                resultadoTransaccion.movimiento,
                resultadoTransaccion.saldoNuevo,
              )
            : previousUsuarioRuta,
        );

        // El prestamo inicial tambien escribe un movimiento de caja.
        void queryClient.invalidateQueries({
          queryKey: queryKeys.movimientos(usuarioRuta.adminUid, usuarioRuta.uid),
        });
        void queryClient.invalidateQueries({
          queryKey: queryKeys.movimientosDelDiaByRuta(usuarioRuta.adminUid, usuarioRuta.uid),
        });

        const clientePayload = crearPayloadCliente(
          datosCliente,
          fechaInicialState,
          imageUrl,
          imageTiendaUrl,
        );

        await guardarClienteEnFirebase(
          {
            ...clientePayload,
            esRenovacion: true,
            perfilCreatedAt: selectedCliente.createdAt,
            perfilUid: selectedCliente.uid,
          },
          usuarioRuta,
          fechaInicialState,
          setInfoClientes,
          infoClientes ?? [],
        );

        // guardarClienteEnFirebase tambien escribe en allClients (renovacion);
        // invalidamos para que una reapertura de este modal no muestre el
        // dato viejo mientras el staleTime de allClients no haya expirado.
        void queryClient.invalidateQueries({
          queryKey: queryKeys.allClients(usuarioRuta.adminUid, usuarioRuta.uid),
        });

        setIsModalCreateClienteExistente(false);
        toast.success("Cliente agregado con exito");
      } catch (error) {
        console.error("Error al crear cliente existente:", error);
        toast.error("No se pudo agregar el cliente");
      } finally {
        setIsSubmiting(false);
      }
    },
    [
      datosCliente,
      fechaInicialState,
      infoClientes,
      queryClient,
      setInfoClientes,
      setIsModalCreateClienteExistente,
      setUsuarioRuta,
      selectedCliente,
      usuarioRuta,
    ],
  );

  return {
    campos,
    crearCliente,
    datosCliente,
    fechaInicialDisplay,
    fechaInicialState,
    filteredClients,
    handleClose,
    handleCuotasPactadasChange,
    handleFechaInicialChange,
    handleFormaDePagoChange,
    handleImageChange,
    handleRemoveImage,
    handleRemoveImageTienda,
    handleImageTiendaChange,
    handlePorcentajeInteresChange,
    handleSearchTermChange,
    handleSelectedCliente,
    handleValorPrestamoChange,
    imagePreview,
    imageTiendaPreview,
    isSubmiting,
    opcionesFormaPago: OPCIONES_FORMA_PAGO,
    selectedCliente,
    showInputSearch,
  };
};
